import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';

import { getDomainBasedApiBaseUrlState, getLocaleState } from '@redux/selectors';

import { Traveller, TravellerType } from '@wegoTypes/traveller';

const getTravellerListKey = 'traveller-list';

type TravellerResponse = {
  data: Traveller;
  message: string;
  status: string;
  success: boolean;
};
type GetTravellersResponse = {
  success: boolean;
  message: string;
  status: string;
  data: {
    list: Traveller[];
    hasMoreData: boolean;
  };
};
type PartialTraveller = {
  firstName: string;
  lastName: string;
  email?: string;
  nationality?: string;
  travellerType: TravellerType;
  dateOfBirth?: string;
  gender?: 'MALE' | 'FEMALE';
  travellerDocuments?: Array<{
    documentNumber: string;
    documentType: string;
    expiryDate?: string;
    issuingCountry?: string;
  }>;
};

const getTravellers = async (locale: string, domainBasedApiBaseUrl: string) => {
  const response = await axios.get<GetTravellersResponse>(
    `${domainBasedApiBaseUrl}/user-centric/authenticated/v2/users/travellers`,
    {
      headers: {
        'Accept-Language': locale,
      },
      withCredentials: true,
    },
  );

  if (response.status === 200) {
    return response.data.data;
  }

  throw new Error(response.statusText);
};
const createTraveller = async (traveller: PartialTraveller, domainBasedApiBaseUrl: string) => {
  const response = await axios.post<TravellerResponse>(
    `${domainBasedApiBaseUrl}/user-centric/authenticated/v2/users/travellers`,
    {
      ...traveller,
    },
    {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data.data;
};
const updateTraveller = async (
  traveller: PartialTraveller & { id: number },
  domainBasedApiBaseUrl: string,
) => {
  const response = await axios.put<TravellerResponse>(
    `${domainBasedApiBaseUrl}/user-centric/authenticated/v2/users/travellers/${traveller.id}`,
    {
      ...traveller,
    },
    {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data.data;
};
const deleteTraveller = async (id: number, domainBasedApiBaseUrl: string) => {
  await axios.delete(
    `${domainBasedApiBaseUrl}/user-centric/authenticated/v2/users/travellers/${id}`,
    {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};
export const useGetTravellers = (userLoggedIn: boolean) => {
  const locale = useSelector(getLocaleState);
  const domainBasedApiBaseUrl = useSelector(getDomainBasedApiBaseUrlState);

  return useQuery({
    queryKey: [getTravellerListKey, { locale }],
    queryFn: () => getTravellers(locale, domainBasedApiBaseUrl),
    enabled: userLoggedIn,
  });
};

export const useCreateTraveller = () => {
  const domainBasedApiBaseUrl = useSelector(getDomainBasedApiBaseUrlState);
  const queryClient = useQueryClient();

  return useMutation<Traveller, Error, PartialTraveller>({
    mutationFn: (traveller: PartialTraveller) => createTraveller(traveller, domainBasedApiBaseUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getTravellerListKey] });
    },
  });
};

export const useUpdateTraveller = () => {
  const domainBasedApiBaseUrl = useSelector(getDomainBasedApiBaseUrlState);
  const queryClient = useQueryClient();

  return useMutation<Traveller, Error, PartialTraveller & { id: number }>({
    mutationFn: (traveller: PartialTraveller & { id: number }) =>
      updateTraveller(traveller, domainBasedApiBaseUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getTravellerListKey] });
    },
  });
};

export const useDeleteTraveller = () => {
  const domainBasedApiBaseUrl = useSelector(getDomainBasedApiBaseUrlState);
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id: number) => deleteTraveller(id, domainBasedApiBaseUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getTravellerListKey] });
    },
  });
};
